import * as React from 'react';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import {
  Typography,
  Paper,
  List,
  ListItem,
  Divider,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Checkbox,
  Collapse,
  ListItemIcon,
  Grid,
  Snackbar,
  CircularProgress,
  Button,
  IconButton
} from '@material-ui/core';

import BackIcon from '@material-ui/icons/ArrowBack';
import KindIcon from '@material-ui/icons/Photo';
import TodayIcon from '@material-ui/icons/Today';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import { withRouter, RouteComponentProps } from 'react-router-dom';

import {
  FarmLibraryProvider,
  LayoutToolsProvider,
  LayoutTools,
  DeleteDocumentProvider,
  FarmFieldsProvider
} from '../providers';

import { DocumentCard, LinkButton, AlertDialog } from '../components';
import { DatePicker } from 'material-ui-pickers';
import { timestampToString } from '../utils';
import inferDocDownloadUrl from '../utils/inferDocDownloadUrl';
import { DeleteFn } from '../providers/DeleteDocumentProvider';
import SidePanel from './SidePanel';

type Props = WithStyles & RouteComponentProps<any>;

interface State {
  kindMenuOpen: boolean;
  kind: string[];
  criticalMonthsOpen: boolean;
  criticalMonths: number[];
  dateMenuOpen: boolean;
  capturedSince: number | null;
  capturedUntil: number | null;
  fieldsOpen: boolean;
  fieldId: string | null;
  deletingDoc: string | null;
}

interface Renders {
  layout: LayoutTools;
  farmId: string;
  farm: Farm | null;
  fields: Field[] | null;
  documents: AnyDocument[] | null;
  loading: boolean;
  fetchMore: () => void;
  deleteDocumentFn: DeleteFn;
  anythingLoading: boolean;
}

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  list: {
    marginBottom: 30
  },
  badge: {
    width: '100%'
  },
  content: {
    padding: theme.spacing.unit * 3,
    flex: 1,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'center'
  }
});

class Library extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      kindMenuOpen: false,
      kind: ['raster-ndvi'],
      criticalMonthsOpen: false,
      criticalMonths: [],
      dateMenuOpen: false,
      capturedSince: null,
      capturedUntil: null,
      fieldsOpen: false,
      fieldId: null,
      deletingDoc: null
    };
  }

  handleToggleMonth(month: number) {
    if (this.state.criticalMonths.find(m => m === month)) {
      this.setState({
        criticalMonths: this.state.criticalMonths.filter(m => m !== month)
      });
    } else {
      this.setState({
        criticalMonths: [...this.state.criticalMonths, month]
      });
    }
  }

  renderMonthCheckbox(monthNumber: number, monthName: string) {
    return (
      <ListItem key={monthNumber} dense={true}>
        <ListItemText primary={monthName} />
        <ListItemSecondaryAction>
          <Checkbox
            checked={this.state.criticalMonths.indexOf(monthNumber) > -1}
            onChange={() => this.handleToggleMonth(monthNumber)}
          />
        </ListItemSecondaryAction>
      </ListItem>
    );
  }

  renderKindCheckbox(label: string, value: string) {
    return (
      <ListItem key={value} dense={true}>
        <ListItemText primary={label} />
        <ListItemSecondaryAction>
          <Switch
            checked={this.state.kind.indexOf(value) > -1}
            onClick={() => this.handleToggleKind(value)}
          />
        </ListItemSecondaryAction>
      </ListItem>
    );
  }

  renderFieldCheckbox(field: Field) {
    return (
      <ListItem key={field.id} dense={true}>
        <ListItemText primary={field.name} />
        <ListItemSecondaryAction>
          <Switch
            checked={this.state.fieldId === field.id}
            onClick={() => this.setState({ fieldId: field.id })}
          />
        </ListItemSecondaryAction>
      </ListItem>
    );
  }

  handleToggleKind(kind: string) {
    if (this.state.kind.indexOf(kind) > -1) {
      this.setState({
        kind: this.state.kind.filter(k => k !== kind)
      });
    } else {
      this.setState({
        kind: [...this.state.kind, kind]
      });
    }
  }

  async handleConfirmDelete(renders: Renders) {
    const { deletingDoc } = this.state;

    if (!deletingDoc) {
      return;
    }

    renders.deleteDocumentFn(deletingDoc);
    await this.setState({ deletingDoc: null });
  }

  handleDocOpen = (renders: Renders, doc: RasterDocument) => {
    renders.layout.pinLayer(doc.fieldId, doc);
    this.props.history.push(`/farms/${renders.farmId}`);
  }

  renderDocument(renders: Renders, doc: AnyDocument) {
    const downloadUrl = inferDocDownloadUrl(doc);
    return (
      <Grid key={doc.id} item={true} xs={12} sm={4}>
        <DocumentCard
          kind={doc.kind}
          source={(doc as RasterDocument).sceneSource}
          meanNdvi={doc.meanNdvi}
          date={timestampToString(doc.capturedOn)}
          thumbnailUrl={doc.thumbnailUrl}
          selectable={false}
          onOpen={() => this.handleDocOpen(renders, doc as RasterDocument)}
          onDownload={downloadUrl ? () => window.open(downloadUrl) : undefined}
          onDelete={() => this.setState({ deletingDoc: doc.id })}
        />
      </Grid>
    );
  }

  renderPanelItems(renders: Renders) {
    const { classes } = this.props;
    
    return (
      <>
        <div style={{display: 'flex'}}>
          <div style={{flex: 0, marginRight: 16, alignItems: 'center'}}>
            <IconButton onClick={() => this.props.history.goBack()}>
              <BackIcon />
            </IconButton>
          </div>
          <div style={{flex: 1}}>
            <Typography variant="subheading">Biblioteca</Typography>
            <Typography variant="headline">
              {renders.farm ? renders.farm.name : ''}
            </Typography>
          </div>
        </div>

        <List component="nav" className={classes.list}>
          <ListItem
            button={true}
            onClick={() =>
              this.setState({
                kindMenuOpen: !this.state.kindMenuOpen
              })
            }
          >
            <ListItemIcon>
              <KindIcon />
            </ListItemIcon>
            <ListItemText primary="Filtrar por Tipo" />
            {this.state.kindMenuOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={this.state.kindMenuOpen}>
            <List>
              {this.renderKindCheckbox('NDVI', 'raster-ndvi')}
              {this.renderKindCheckbox('Falso Color', 'raster-falsecolor')}
              {this.renderKindCheckbox('Color Verdadero', 'raster-truecolor')}
              {this.renderKindCheckbox('Escenas', 'scene')}
              {this.renderKindCheckbox(
                'Análisis de Productividad',
                'yield-analysis'
              )}
              {this.renderKindCheckbox('Zonas de Gestión', 'zone-analysis')}
            </List>
          </Collapse>

          <Divider />

          <ListItem
            button={true}
            onClick={() =>
              this.setState({
                dateMenuOpen: !this.state.dateMenuOpen
              })
            }
          >
            <ListItemIcon>
              <TodayIcon />
            </ListItemIcon>
            <ListItemText primary="Filtrar por Fecha" />
            {this.state.dateMenuOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={this.state.dateMenuOpen}>
            <List>
              <ListItem>
                <DatePicker
                  fullWidth={true}
                  label="Desde"
                  clearable={true}
                  disableFuture={true}
                  value={this.state.capturedSince}
                  onChange={d => this.setState({ capturedSince: d })}
                  maxDate={this.state.capturedUntil || undefined}
                  clearLabel="Limpiar"
                  labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
                />
              </ListItem>
              <ListItem>
                <DatePicker
                  fullWidth={true}
                  label="Hasta"
                  clearable={true}
                  disableFuture={true}
                  value={this.state.capturedUntil}
                  onChange={d => this.setState({ capturedUntil: d })}
                  minDate={this.state.capturedSince || undefined}
                  clearLabel="Limpiar"
                  labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
                />
              </ListItem>
            </List>
          </Collapse>

          <Divider />

          <ListItem
            button={true}
            onClick={() =>
              this.setState({
                criticalMonthsOpen: !this.state.criticalMonthsOpen
              })
            }
          >
            <ListItemIcon>
              <TodayIcon />
            </ListItemIcon>
            <ListItemText primary="Meses críticos" />
            {this.state.criticalMonthsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={this.state.criticalMonthsOpen}>
            <List style={{ maxHeight: 250, overflowY: 'scroll' }}>
              {this.renderMonthCheckbox(1, 'Enero')}
              {this.renderMonthCheckbox(2, 'Febrero')}
              {this.renderMonthCheckbox(3, 'Marzo')}
              {this.renderMonthCheckbox(4, 'Abril')}
              {this.renderMonthCheckbox(5, 'Mayo')}
              {this.renderMonthCheckbox(6, 'Junio')}
              {this.renderMonthCheckbox(7, 'Julio')}
              {this.renderMonthCheckbox(8, 'Agosto')}
              {this.renderMonthCheckbox(9, 'Septiembre')}
              {this.renderMonthCheckbox(10, 'Octubre')}
              {this.renderMonthCheckbox(11, 'Noviembre')}
              {this.renderMonthCheckbox(12, 'Diciembre')}
            </List>
          </Collapse>

          <Divider />

          <ListItem
            button={true}
            onClick={() =>
              this.setState({
                fieldsOpen: !this.state.fieldsOpen
              })
            }
          >
            <ListItemIcon>
              <TodayIcon />
            </ListItemIcon>
            <ListItemText primary="Lotes" />
            {this.state.fieldsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={this.state.fieldsOpen}>
            <List style={{ maxHeight: 250, overflowY: 'scroll' }}>
              {renders.fields &&
                renders.fields.map(f => this.renderFieldCheckbox(f))}
            </List>
          </Collapse>
        </List>
        <LinkButton
          fullWidth={true}
          variant="raised"
          to={`/scenes/${renders.farmId}`}
          style={{ marginTop: '20px' }}
        >
          Agregar Escenas
        </LinkButton>
        <Button
          fullWidth={true}
          variant="raised"
          onClick={() => renders.layout.showBetaDialog()}
          style={{ marginTop: '20px' }}
        >
          Importar Datos Propios
        </Button>
      </>
    );
  }

  renderLoading(renders: Renders) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={renders.anythingLoading}
        message={
          <>
            <CircularProgress color="secondary" size={25} />
            <span>cargando</span>
          </>
        }
      />
    );
  }

  renderFetchMoreButton(renders: Renders) {
    return (
      <Grid item={true} xs={12} style={{ textAlign: 'center', marginTop: 10 }}>
        <Button onClick={renders.fetchMore} disabled={renders.anythingLoading}>
          Cargar Más
        </Button>
      </Grid>
    );
  }

  composeProviders(children: (renders: Renders) => React.ReactNode) {
    const {
      match: {
        params: { farmId }
      }
    } = this.props;

    const { kind, criticalMonths, capturedSince, capturedUntil, fieldId } = this.state;

    return (
      <LayoutToolsProvider>
        {layout => (
          <FarmFieldsProvider farmId={farmId}>
            {fieldsResult => (
              <FarmLibraryProvider
                farmId={farmId}
                first={9}
                filter={{ kind, criticalMonths, capturedSince, capturedUntil, fieldId }}
              >
                {result => (
                  <DeleteDocumentProvider onCompleted={result.refetch}>
                    {(deleteDocumentFn, deleting) =>
                      children({
                        farmId,
                        layout,
                        farm: result.farm,
                        fields: fieldsResult.fields,
                        documents: result.documents,
                        loading: result.loading,
                        fetchMore: result.fetchMore,
                        deleteDocumentFn,
                        anythingLoading:
                          fieldsResult.loading || result.loading || deleting
                      })
                    }
                  </DeleteDocumentProvider>
                )}
              </FarmLibraryProvider>
            )}
          </FarmFieldsProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  render() {
    const { classes } = this.props;
    const { deletingDoc } = this.state;

    return this.composeProviders(renders => (
      <main className={classes.root}>
        <SidePanel>{this.renderPanelItems(renders)}</SidePanel>
        <Paper square={true} elevation={1} className={classes.content}>
          <Grid container={true} spacing={24} style={{ maxWidth: 900 }}>
            {this.renderLoading(renders)}
            {renders.documents &&
              renders.documents.map(doc => this.renderDocument(renders, doc))}
            {renders.documents && this.renderFetchMoreButton(renders)}
          </Grid>
        </Paper>
        <AlertDialog
          title="Eliminar Documento"
          message="Esta seguro que desea eliminar el documento?"
          open={deletingDoc != null}
          onCancelClick={() => this.setState({ deletingDoc: null })}
          onContinueClick={() => this.handleConfirmDelete(renders)}
        />
      </main>
    ));
  }
}

export default withStyles(styles)(withRouter(Library));



// WEBPACK FOOTER //
// ./src/containers/Library.tsx